"use client";

import { useEffect } from "react";
import { useRouter } from "next/navigation";

/**
 * Prefetches every top-level route as soon as the layout has mounted
 * and the browser is idle. Without this, the first SPA navigation
 * after landing sometimes fires before Next has finished fetching
 * the target route's JS chunk + RSC payload — React tries to mount
 * the incomplete tree, throws `removeChild` / `insertBefore` during
 * the commit, and the error boundary takes over. After the crash-
 * triggered refresh the browser has the chunks cached, so navigation
 * works from then on. Priming them up front removes that race.
 *
 * Uses `requestIdleCallback` so the prefetch doesn't compete with
 * the initial page's critical render work.
 */
const ROUTES = [
  "/",
  "/about",
  "/books",
  "/blog",
  "/contact",
  "/terms",
  "/privacy",
];

export default function RoutePrefetch() {
  const router = useRouter();

  useEffect(() => {
    const run = () => {
      for (const route of ROUTES) {
        try {
          router.prefetch(route);
        } catch {}
      }
    };

    if (typeof window === "undefined") return undefined;

    let idleId = 0;
    if ("requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(run, { timeout: 2000 });
    } else {
      idleId = window.setTimeout(run, 600);
    }

    return () => {
      if ("cancelIdleCallback" in window && idleId) {
        window.cancelIdleCallback(idleId);
      } else if (idleId) {
        window.clearTimeout(idleId);
      }
    };
  }, [router]);

  return null;
}
